import { DatePipe, NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { IOnuStatusSignal } from '@modules/clients/services/olt/models/onu-view.model';
import { OnuViewService } from '@modules/clients/services/olt/onu-view.service';

/** Colores e íconos del estado, los mismos de SmartOLT. */
const STATUS_PILLS: Record<string, { color: string; icon: string }> = {
  Online: { color: '#1fb325', icon: 'pi pi-globe' },
  'Power fail': { color: '#0064C8', icon: 'pi pi-bolt' },
  LOS: { color: '#dc2626', icon: 'pi pi-link' },
  Offline: { color: '#4c4b4b', icon: 'pi pi-question' },
};

// LIVE de la tarjeta de SmartOLT: lee la óptica cada 2 s durante 60 s
const LIVE_DURATION_MS = 60000;
const LIVE_POLL_MS = 2000;

// Escala de las barras de señal de SmartOLT: de -36 a -12 dBm
const SCALE_MIN = -36;
const SCALE_MAX = -12;

const SIGNAL_COLORS = { good: '#1fb325', warning: '#f97316', critical: '#ef4444' };

/** Calificación de la señal que devuelve SmartOLT, en español. */
const SIGNAL_QUALITY: Record<string, string> = {
  'Very good': 'Muy buena',
  Good: 'Buena',
  Warning: 'Advertencia',
  Critical: 'Crítica',
};

export interface ISignalMeter {
  name: string;
  /** dirección de la luz: OLT → ONU (1490) u ONU → OLT (1310) */
  direction: string;
  value: number | null;
  color: string;
  level: string;
  /** posición del marcador en la barra (%) */
  position: number;
}

/**
 * Tarjeta de estado y niveles Rx de la ONU, como la de onu/view de SmartOLT:
 * estado con su tiempo, distancia, barras con los umbrales y el detalle óptico desplegable.
 */
@Component({
  selector: 'app-onu-status-panel',
  standalone: true,
  imports: [DatePipe, NgClass],
  templateUrl: './onu-status-panel.component.html',
})
export class OnuStatusPanelComponent implements OnDestroy {
  @Input({ required: true }) signal!: IOnuStatusSignal;
  @Input({ required: true }) onuId!: number;
  /** cada lectura nueva, para que el modal tenga el estado al día */
  @Output() signalChange = new EventEmitter<IOnuStatusSignal>();
  /** abre el modal de tráfico en vivo */
  @Output() trafficLive = new EventEmitter<void>();

  public expanded = false;
  public loading = false;
  /** la OLT no respondió la última lectura */
  public noAnswer = false;
  /** segundos que le quedan al LIVE; 0 = apagado */
  public liveRemaining = 0;

  private liveUntil = 0;
  private liveTicker?: ReturnType<typeof setInterval>;
  private liveTimer?: ReturnType<typeof setTimeout>;

  constructor(private readonly onuViewService: OnuViewService) {}

  ngOnDestroy(): void {
    this.stopLive();
  }

  /**
   * readOptics: lee estado, Rx, Tx y atenuación directamente de la OLT
   * (el Refresh de la tarjeta de SmartOLT)
   */
  public async readOptics(): Promise<void> {
    this.loading = true;
    try {
      const response = await this.onuViewService.read<
        IOnuStatusSignal & { olt_read_skipped?: boolean }
      >(this.onuId, 'status_optics');
      const data = response.data;
      // una lectura que la OLT no hizo no trae valores: se conservan los que se muestran
      this.noAnswer = !data || !!data.olt_read_skipped;
      if (!this.noAnswer) {
        this.signal = { ...this.signal, ...data };
        this.signalChange.emit(this.signal);
      }
    } catch (error) {
      this.noAnswer = true;
    } finally {
      this.loading = false;
    }
  }

  /**
   * toggleLive: LIVE de la tarjeta de SmartOLT, lecturas ópticas seguidas durante 60 s
   */
  public toggleLive() {
    if (this.liveUntil) {
      this.stopLive();
      return;
    }
    this.expanded = true;
    this.liveUntil = Date.now() + LIVE_DURATION_MS;
    this.tick();
    this.liveTicker = setInterval(() => this.tick(), 1000);
    this.poll();
  }

  public stopLive() {
    clearInterval(this.liveTicker);
    clearTimeout(this.liveTimer);
    this.liveTicker = undefined;
    this.liveTimer = undefined;
    this.liveUntil = 0;
    this.liveRemaining = 0;
  }

  private tick() {
    this.liveRemaining = Math.max(0, Math.ceil((this.liveUntil - Date.now()) / 1000));
    if (this.liveRemaining <= 0) this.stopLive();
  }

  // la siguiente lectura sale 2 s después de que termina la anterior, como en SmartOLT
  private async poll() {
    if (!this.liveUntil) return;
    await this.readOptics();
    if (!this.liveUntil || Date.now() >= this.liveUntil) {
      this.stopLive();
      return;
    }
    this.liveTimer = setTimeout(() => this.poll(), LIVE_POLL_MS);
  }

  public get pill() {
    return STATUS_PILLS[this.signal.onu_status] ?? STATUS_PILLS['Offline'];
  }

  /** Tiempo en el estado actual: "9h 22m" o "407d 13h". */
  public get duration(): string {
    const since = this.signal.last_status_change_unix;
    if (!since) return this.signal.last_status_change ?? '';
    const now = this.signal.server_time_unix ?? Math.floor(Date.now() / 1000);
    const minutes = Math.max(0, Math.floor((now - since) / 60));
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    return days > 0 ? `${days}d ${hours}h` : `${hours}h ${String(minutes % 60).padStart(2, '0')}m`;
  }

  public get sinceDate(): Date | null {
    return this.signal.last_status_change_unix
      ? new Date(this.signal.last_status_change_unix * 1000)
      : null;
  }

  /** Distancia en km; la OLT la reporta en metros. */
  public get distance(): string {
    const meters = parseFloat(String(this.signal.distance ?? ''));
    return Number.isNaN(meters) ? '' : `${(meters / 1000).toFixed(2)} km`;
  }

  /** Fondo de la barra: rojo hasta el umbral crítico, naranja hasta warning y luego verde. */
  public get barBackground(): string {
    const critical = this.percent(this.signal.signal_limit_critical);
    const warning = this.percent(this.signal.signal_limit_warning);
    return (
      `linear-gradient(to right, ${SIGNAL_COLORS.critical} ${critical}%, ${SIGNAL_COLORS.warning} ${critical}%, ` +
      `${SIGNAL_COLORS.warning} ${warning}%, ${SIGNAL_COLORS.good} ${warning}%)`
    );
  }

  public get meters(): ISignalMeter[] {
    return [
      this.meter('ONU Rx 1490nm', 'OLT → ONU', this.signal.onu_signal_rx1490),
      this.meter('OLT Rx 1310nm', 'ONU → OLT', this.signal.onu_signal_rx1310),
    ];
  }

  public get quality(): string {
    return SIGNAL_QUALITY[this.signal.onu_signal] ?? this.signal.onu_signal ?? '';
  }

  public get limits(): string {
    return `Warning ≤ ${this.signal.signal_limit_warning} dBm · Critical ≤ ${this.signal.signal_limit_critical} dBm`;
  }

  public colorOf(value: string | number | null | undefined): string {
    const dbm = this.toNumber(value);
    if (dbm === null) return '';
    if (dbm <= this.signal.signal_limit_critical) return SIGNAL_COLORS.critical;
    if (dbm <= this.signal.signal_limit_warning) return SIGNAL_COLORS.warning;
    return SIGNAL_COLORS.good;
  }

  public format(value: string | number | null | undefined): string {
    const n = this.toNumber(value);
    return n === null ? '–' : n.toFixed(2);
  }

  private meter(name: string, direction: string, raw: string | number | null): ISignalMeter {
    const value = this.toNumber(raw);
    const color = this.colorOf(value);
    return {
      name,
      direction,
      value,
      color,
      level:
        value === null
          ? 'Sin lectura'
          : color === SIGNAL_COLORS.critical
          ? 'Crítica'
          : color === SIGNAL_COLORS.warning
          ? 'Advertencia'
          : 'Buena',
      position: value === null ? -1 : Math.min(100, Math.max(0, this.percent(value))),
    };
  }

  private percent(dbm: number): number {
    return ((dbm - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100;
  }

  private toNumber(value: string | number | null | undefined): number | null {
    const n = parseFloat(String(value ?? ''));
    return Number.isNaN(n) ? null : n;
  }
}
